/** Dashboard del gestore multi azienda: riepilogo, elenco con "Da fare", aggiunta e pagamento. */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  CreditCard,
  FileSignature,
  KeyRound,
  Loader2,
  LogIn,
  Pencil,
  Plus,
  Search,
  Tag,
  Trash2,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";

import {
  apiErrorMessage,
  endpoints,
  type GestoreAzienda,
  type GestorePreventivo,
  type NuovaAziendaIn,
} from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CenteredLoader, ErrorState } from "@/components/ui/feedback";
import { clearAziendaSilently, enterAzienda, getAziendaId } from "@/lib/azienda";

const eur = (cents: number) =>
  (cents / 100).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("it-IT") : "—";

type Filtro = "tutte" | "attive" | "da_fare" | "in_disattivazione" | "in_attesa";

const STATUS_LABEL: Record<GestoreAzienda["status"], { label: string; cls: string }> = {
  active: { label: "Attiva", cls: "bg-emerald-100 text-emerald-800" },
  deactivating: { label: "In disattivazione", cls: "bg-amber-100 text-amber-800" },
  pending_payment: { label: "In attesa di pagamento", cls: "bg-slate-200 text-slate-700" },
  removed: { label: "Rimossa", cls: "bg-slate-200 text-slate-500" },
};

export function GestoreAziende() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const success = params.get("success") === "1";
  const canceled = params.get("canceled") === "1";

  // Questa pagina lavora sempre come gestore: se era selezionata un'azienda la si lascia.
  useEffect(() => {
    if (getAziendaId()) {
      clearAziendaSilently();
      qc.invalidateQueries();
    }
  }, [qc]);

  const q = useQuery({
    queryKey: ["gestore-aziende"],
    queryFn: endpoints.gestore.aziende,
    refetchOnMount: "always",
    refetchInterval: success ? 5_000 : false,
  });

  useEffect(() => {
    if (success && q.data && !q.data.aziende.some((a) => a.status === "pending_payment")) {
      toast.success("Pagamento ricevuto: le aziende sono attive.");
      setParams({}, { replace: true });
      qc.invalidateQueries({ queryKey: ["billing-status"] });
    }
  }, [success, q.data, setParams, qc]);

  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("tutte");
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<GestoreAzienda | null>(null);

  const disattivaMut = useMutation({
    mutationFn: (id: string) => endpoints.gestore.disattiva(id),
    onSuccess: () => {
      toast.success("Azienda in disattivazione: resta usabile fino alla scadenza.");
      qc.invalidateQueries({ queryKey: ["gestore-aziende"] });
    },
    onError: (e: unknown) => toast.error(apiErrorMessage(e)),
  });
  const riattivaMut = useMutation({
    mutationFn: (id: string) => endpoints.gestore.riattiva(id),
    onSuccess: () => {
      toast.success("Azienda riattivata.");
      qc.invalidateQueries({ queryKey: ["gestore-aziende"] });
    },
    onError: (e: unknown) => toast.error(apiErrorMessage(e)),
  });

  const aziende = q.data?.aziende ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return aziende.filter((a) => {
      if (s && !`${a.ragione_sociale} ${a.piva ?? ""}`.toLowerCase().includes(s)) return false;
      switch (filtro) {
        case "attive":
          return a.status === "active";
        case "in_disattivazione":
          return a.status === "deactivating";
        case "in_attesa":
          return a.status === "pending_payment";
        case "da_fare":
          return (
            a.status !== "pending_payment" &&
            (a.da_fare.certificato || a.da_fare.firme || a.da_fare.cer_over_quota > 0)
          );
        default:
          return true;
      }
    });
  }, [aziende, search, filtro]);

  if (q.isLoading) return <CenteredLoader label="Carico le tue aziende…" />;
  if (q.isError) return <ErrorState title="Impossibile caricare le aziende" error={q.error} />;
  const r = q.data!.riepilogo;
  const pending = aziende.filter((a) => a.status === "pending_payment");
  const daFare = aziende.filter(
    (a) =>
      a.status !== "pending_payment" &&
      (a.da_fare.certificato || a.da_fare.firme || a.da_fare.cer_over_quota > 0)
  ).length;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Building2 className="h-6 w-6 text-primary" /> Le mie aziende
          </h1>
          <p className="text-sm text-muted-foreground">
            Un solo abbonamento, una scadenza comune e un prezzo per azienda che cala con il numero
            di aziende attive.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Aggiungi aziende
        </Button>
      </div>

      {canceled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Pagamento annullato. Le aziende inserite restano in attesa: riprova quando vuoi.
        </div>
      )}
      {success && pending.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <Loader2 className="h-4 w-4 animate-spin" /> Conferma del pagamento in corso…
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Aziende attive" value={String(r.aziende_attive)} hint={
          r.aziende_in_disattivazione ? `${r.aziende_in_disattivazione} in disattivazione` : undefined
        } />
        <Stat
          label="Scaglione attuale"
          value={r.scaglione ? `${eur(r.scaglione.prezzo_netto_cents)} + IVA` : "—"}
          hint={r.scaglione ? `per azienda/anno (${tierLabel(r.scaglione.da, r.scaglione.a)})` : "nessuna azienda attiva"}
        />
        <Stat label="Scadenza comune" value={fmtDate(r.scadenza_comune)} hint={subLabel(r.subscription_status)} />
        <Stat
          label="Rinnovo stimato"
          value={r.rinnovo_stimato_netto_cents ? `${eur(r.rinnovo_stimato_netto_cents)} + IVA` : "—"}
          hint={daFare ? `${daFare} aziende con cose da fare` : "tutto in ordine"}
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Cerca per ragione sociale o P.IVA"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="rounded-md border bg-background px-2 py-2 text-sm"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value as Filtro)}
              aria-label="Filtro stato"
            >
              <option value="tutte">Tutte</option>
              <option value="attive">Attive</option>
              <option value="da_fare">Con cose da fare</option>
              <option value="in_disattivazione">In disattivazione</option>
              <option value="in_attesa">In attesa di pagamento</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              {aziende.length === 0
                ? "Nessuna azienda ancora: aggiungi la prima per attivare l'abbonamento."
                : "Nessuna azienda corrisponde ai filtri."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Azienda</th>
                  <th className="px-4 py-2">Stato</th>
                  <th className="px-4 py-2">Da fare</th>
                  <th className="px-4 py-2">Firma documenti</th>
                  <th className="px-4 py-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium">{a.ragione_sociale}</div>
                      <div className="text-xs text-muted-foreground">
                        {a.piva ? `P.IVA ${a.piva}` : "P.IVA non indicata"}
                        {a.status === "deactivating" && a.active_until && ` · fino al ${fmtDate(a.active_until)}`}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_LABEL[a.status].cls}`}>
                        {STATUS_LABEL[a.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <DaFare a={a} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {a.email_firma || "il gestore"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {a.status !== "pending_payment" && (
                          <Button size="sm" onClick={() => enterAzienda(a.id)}>
                            <LogIn className="mr-1 h-4 w-4" /> Entra
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setEditing(a)} title="Modifica">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {a.status === "active" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Disattiva al rinnovo"
                            disabled={disattivaMut.isPending}
                            onClick={() => {
                              if (confirm(`Disattivare ${a.ragione_sociale}? Resta usabile fino alla scadenza e non sarà rinnovata.`))
                                disattivaMut.mutate(a.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                        {a.status === "deactivating" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Riattiva"
                            disabled={riattivaMut.isPending}
                            onClick={() => riattivaMut.mutate(a.id)}
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Listino per azienda (annuale, + IVA)</CardTitle>
          <CardDescription>
            Lo scaglione si applica a tutte le aziende attive in base al loro numero. Ogni azienda ha
            il proprio certificato, 5 CER inclusi, i pacchetti CER e le firme dei documenti.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-4">
          {r.scaglioni.map((t) => (
            <div
              key={t.da}
              className={`rounded-md border p-3 text-sm ${r.scaglione?.da === t.da ? "border-primary bg-primary/5" : ""}`}
            >
              <div className="text-xs text-muted-foreground">{tierLabel(t.da, t.a)}</div>
              <div className="text-lg font-semibold">{eur(t.prezzo_netto_cents)}</div>
            </div>
          ))}
          <div className="rounded-md border border-dashed p-3 text-sm">
            <div className="text-xs text-muted-foreground">Oltre {r.max_aziende} aziende</div>
            <div className="text-sm font-medium">
              Offerta su richiesta:{" "}
              <a className="underline" href={`mailto:${r.contatto_offerte}`}>{r.contatto_offerte}</a>
            </div>
          </div>
        </CardContent>
      </Card>

      <AddDialog open={addOpen} onClose={() => setAddOpen(false)} pending={pending} />
      <EditDialog azienda={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function subLabel(status: string | null) {
  switch (status) {
    case "active":
      return "abbonamento attivo";
    case "trialing":
      return "attivo · primo periodo promozionale";
    case "past_due":
      return "pagamento in sospeso";
    case null:
      return undefined;
    default:
      return `abbonamento ${status}`;
  }
}

function tierLabel(da: number, a: number | null) {
  if (da === 1 && a === 1) return "1 azienda";
  return a ? `${da}–${a} aziende` : `${da}+ aziende`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase text-muted-foreground">{label}</div>
        <div className="mt-1 text-xl font-semibold">{value}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function DaFare({ a }: { a: GestoreAzienda }) {
  if (a.status === "pending_payment") return <span className="text-xs text-muted-foreground">—</span>;
  const items: { icon: typeof KeyRound; label: string }[] = [];
  if (a.da_fare.certificato) items.push({ icon: KeyRound, label: "Certificato RENTRI mancante" });
  if (a.da_fare.firme) items.push({ icon: FileSignature, label: "Documenti da firmare" });
  if (a.da_fare.cer_over_quota > 0)
    items.push({ icon: Tag, label: `${a.da_fare.cer_over_quota} CER oltre quota` });
  if (items.length === 0)
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" /> In ordine
      </span>
    );
  return (
    <ul className="space-y-0.5">
      {items.map((i) => (
        <li key={i.label} className="flex items-center gap-1 text-xs text-amber-800">
          <i.icon className="h-3.5 w-3.5" /> {i.label}
        </li>
      ))}
    </ul>
  );
}

const emptyRow = (): NuovaAziendaIn => ({ ragione_sociale: "", piva: "", email_firma: "" });

function AddDialog({
  open,
  onClose,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  pending: GestoreAzienda[];
}) {
  const [rows, setRows] = useState<NuovaAziendaIn[]>([emptyRow()]);
  const valid = rows.filter((r) => r.ragione_sociale.trim().length > 0);
  const n = valid.length;

  const prevQ = useQuery({
    queryKey: ["gestore-preventivo", n],
    queryFn: () => endpoints.gestore.preventivo(n),
    enabled: open && n > 0,
    staleTime: 30_000,
    retry: false,
  });

  const checkoutMut = useMutation({
    mutationFn: () => {
      const origin = window.location.origin;
      return endpoints.gestore.checkout({
        aziende: valid.map((r) => ({
          ragione_sociale: r.ragione_sociale.trim(),
          piva: r.piva?.trim() || null,
          email_firma: r.email_firma?.trim() || null,
        })),
        success_url: `${origin}/gestore?success=1`,
        cancel_url: `${origin}/gestore?canceled=1`,
      });
    },
    onSuccess: (d) => {
      window.location.href = d.url;
    },
    onError: (e: unknown) => toast.error(apiErrorMessage(e)),
  });

  const update = (i: number, patch: Partial<NuovaAziendaIn>) =>
    setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  const p: GestorePreventivo | undefined = prevQ.data;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Aggiungi aziende</DialogTitle>
          <DialogDescription>
            Inserisci una o più aziende. L&apos;email per la firma è facoltativa: se vuota firmi tu i
            documenti (nomina conservazione e contratto GDPR).
          </DialogDescription>
        </DialogHeader>

        {pending.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Hai {pending.length} aziende in attesa di pagamento ({pending.map((a) => a.ragione_sociale).join(", ")}).
              Se completi un nuovo pagamento verranno attivate solo quelle di questo elenco.
            </span>
          </div>
        )}

        <div className="max-h-[40vh] space-y-2 overflow-y-auto pr-1">
          <div className="grid grid-cols-[1fr_140px_1fr_32px] gap-2 text-xs text-muted-foreground">
            <span>Ragione sociale *</span>
            <span>P.IVA</span>
            <span>Email per la firma (facoltativa)</span>
            <span />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_140px_1fr_32px] gap-2">
              <Input
                value={r.ragione_sociale}
                onChange={(e) => update(i, { ragione_sociale: e.target.value })}
                placeholder="Es. Rossi Srl"
                autoFocus={i === rows.length - 1}
              />
              <Input
                value={r.piva ?? ""}
                onChange={(e) => update(i, { piva: e.target.value })}
                placeholder="11 cifre"
                inputMode="numeric"
              />
              <Input
                value={r.email_firma ?? ""}
                onChange={(e) => update(i, { email_firma: e.target.value })}
                placeholder="referente@azienda.it"
                type="email"
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, k) => k !== i) : rs))}
                disabled={rows.length === 1}
                title="Rimuovi riga"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, emptyRow()])}>
          <Plus className="mr-1 h-4 w-4" /> Altra azienda
        </Button>

        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          {n === 0 ? (
            <span className="text-muted-foreground">Inserisci almeno una ragione sociale.</span>
          ) : prevQ.isError ? (
            <span className="flex items-start gap-2 text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {apiErrorMessage(prevQ.error)}
            </span>
          ) : prevQ.isLoading || !p ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Calcolo del prezzo…
            </span>
          ) : (
            <div className="space-y-1">
              <div className="flex justify-between">
                <span>
                  {p.aziende_totali} aziende totali → scaglione {tierLabel(p.scaglione.da, p.scaglione.a)}:{" "}
                  <strong>{eur(p.scaglione.prezzo_netto_cents)} + IVA</strong> per azienda/anno
                  {p.cambia_scaglione && (
                    <Badge variant="secondary" className="ml-2">nuovo scaglione per tutte</Badge>
                  )}
                </span>
              </div>
              {p.kind === "multi_add" && (
                <div className="text-xs text-muted-foreground">
                  Paghi solo i {p.giorni_rimanenti} giorni fino alla scadenza comune del {fmtDate(p.scadenza_comune)}.
                  Al rinnovo: {eur(p.rinnovo_annuo_netto_cents)} + IVA per {p.aziende_totali} aziende.
                </div>
              )}
              <div className="flex items-baseline justify-between border-t pt-2">
                <span>Da pagare ora</span>
                <span className="text-lg font-semibold">
                  {eur(p.totale_cents)}{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    ({eur(p.imponibile_cents)} + IVA 22%)
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button
            onClick={() => checkoutMut.mutate()}
            disabled={n === 0 || !p || checkoutMut.isPending}
          >
            {checkoutMut.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="mr-2 h-4 w-4" />
            )}
            Paga con carta{p ? ` — ${eur(p.totale_cents)}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ azienda, onClose }: { azienda: GestoreAzienda | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<NuovaAziendaIn>(emptyRow());
  useEffect(() => {
    if (azienda)
      setForm({
        ragione_sociale: azienda.ragione_sociale,
        piva: azienda.piva ?? "",
        email_firma: azienda.email_firma ?? "",
      });
  }, [azienda]);

  const mut = useMutation({
    mutationFn: () =>
      endpoints.gestore.aggiorna(azienda!.id, {
        ragione_sociale: form.ragione_sociale.trim(),
        piva: form.piva?.trim() || null,
        email_firma: form.email_firma?.trim() || null,
      }),
    onSuccess: () => {
      toast.success("Azienda aggiornata.");
      qc.invalidateQueries({ queryKey: ["gestore-aziende"] });
      onClose();
    },
    onError: (e: unknown) => toast.error(apiErrorMessage(e)),
  });

  return (
    <Dialog open={Boolean(azienda)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifica azienda</DialogTitle>
          <DialogDescription>
            L&apos;email per la firma riceve i documenti da firmare; se vuota li firma il gestore.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm">
            Ragione sociale
            <Input
              value={form.ragione_sociale}
              onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            P.IVA
            <Input value={form.piva ?? ""} onChange={(e) => setForm({ ...form, piva: e.target.value })} />
          </label>
          <label className="block text-sm">
            Email per la firma dei documenti
            <Input
              type="email"
              value={form.email_firma ?? ""}
              onChange={(e) => setForm({ ...form, email_firma: e.target.value })}
            />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !form.ragione_sociale.trim()}>
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
